'use client'
import { MediaBlock, RichText } from '@/payload-types'
import { useEffect, useRef, useState } from 'react'
import { ImageComponent } from '../../media/imageComponent'
import GalleryCaption from './embeddedGalleryCaption'
import FlowiteIconComponent from '@/components/flowbite-icon/flowbiteIcon'
import VideoComponent from '@/components/media/videoComponent'
import Image from 'next/image'
import { getImageCaption } from '../media-carousel/mediaCarousel'

interface IProps {
  mediaBlockes?: (RichText | MediaBlock)[] | undefined | null
  onImageClick?: (imageSrc: string) => void
}

export const EmbeddedGalleryComponent = ({ mediaBlockes, onImageClick }: IProps) => {
  const [featuredImageIndex, setSelectedImage] = useState(0)
  const [captionVisibility, setCaptionVisibility] = useState<Record<number, boolean>>({})
  const hideCaption = (index: number) => {
    console.log('hideCaption', index)

    setCaptionVisibility(prev => ({
      ...prev,
      [index]: false
    }))
  }

  const isCaptionVisible = (index: number) => {
    return captionVisibility[index] !== false
  }

  const [currentIndex, setCurrentIndex] = useState(0)
  const containerRef = useRef<HTMLDivElement>(null)

  const canGoNext = (currentIndex + 1) * 5 < (mediaBlockes?.length ?? 0)
  const canGoPrev = currentIndex > 0

  const handleNext = () => {
    if (canGoNext) {
      const nextIndex = currentIndex + 1
      setCurrentIndex(nextIndex)
    }
  }

  const handlePrev = () => {
    if (canGoPrev) {
      const prevIndex = Math.max(currentIndex - 1, 0)
      setCurrentIndex(prevIndex)
    }
  }

  useEffect(() => {
    if (containerRef.current) {
      const gap = 8
      const scrollAmount =
        currentIndex * (containerRef.current.offsetWidth + (currentIndex > 0 ? gap : 0))
      const maxScrollAmount =
        scrollAmount + containerRef.current.offsetWidth < containerRef.current.scrollWidth
          ? scrollAmount
          : containerRef.current.scrollWidth - containerRef.current.offsetWidth
      containerRef.current.style.transform = `translateX(-${maxScrollAmount}px)`
    }
  }, [currentIndex])

  if (!mediaBlockes) return null

  return (
    <div className="flex flex-col gap-2">
      <div className="w-full h-[185px] sm:!h-[330px] border-gray-200 rounded-lg">
        {mediaBlockes?.map((item, index) => (
          <div
            key={index}
            className={`${
              index === featuredImageIndex ? '' : mediaBlockes?.length > 2 ? 'hidden' : ''
            } h-full`}
          >
            {item.blockType === 'mediaBlock' &&
              typeof item.media === 'object' &&
              (item.media.mimeType?.startsWith('image/') ? (
                <figure className="inline-block m-0 relative w-full h-full">
                  <div
                    className={`w-full h-full ${onImageClick ? 'cursor-pointer' : ''}`}
                    onClick={() =>
                      typeof item.media === 'object' && item.media.mimeType?.startsWith('image/')
                        ? onImageClick?.(
                            `${
                              process.env.NEXT_PUBLIC_PAYLOAD_URL
                                ? process.env.NEXT_PUBLIC_PAYLOAD_URL
                                : ''
                            }${item.media.sizes?.webp?.url}`,
                          )
                        : undefined
                    }
                  >
                    <ImageComponent
                      src={`${
                        process.env.NEXT_PUBLIC_PAYLOAD_URL
                          ? process.env.NEXT_PUBLIC_PAYLOAD_URL
                          : ''
                      }${item.media.sizes?.webp?.url}`}
                      alt={item.altText ? item.altText : item.media.title}
                      className="!object-contain"
                      width={item.media.sizes?.webp?.width}
                      height={item.media.sizes?.webp?.height}
                    />
                  </div>
                  {item.caption ? (
                    <GalleryCaption
                      imageCaption={item.caption}
                      showCaptions={isCaptionVisible(index)}
                      hideCaptions={() => hideCaption(index)}
                    />
                  ) : (
                    item.media.originalUrl && (
                      <GalleryCaption
                        imageCaption={getImageCaption(item.media)}
                        showCaptions={isCaptionVisible(index)}
                        hideCaptions={() => hideCaption(index)}
                      />
                    )
                  )}
                </figure>
              ) : (
                <div className="w-full h-[185px] sm:!h-[330px] border border-gray-200 rounded-lg">
                  <figure className="inline-block m-0 relative w-full !h-full">
                    <div className="w-full h-[185px] sm:!h-[330px]">
                      {index === featuredImageIndex && <VideoComponent videoData={item.media} />}
                    </div>
                    {item.caption ? (
                      <GalleryCaption
                        imageCaption={item.caption}
                        showCaptions={isCaptionVisible(index)}
                        hideCaptions={() => hideCaption(index)}
                      />
                    ) : (
                      item.media.originalUrl && (
                        <GalleryCaption
                          imageCaption={getImageCaption(item.media)}
                          showCaptions={isCaptionVisible(index)}
                          hideCaptions={() => hideCaption(index)}
                        />
                      )
                    )}
                  </figure>
                </div>
              ))}
          </div>
        ))}
      </div>
      {mediaBlockes?.length > 2 && (
        <div className="w-full relative">
          <div className="overflow-hidden w-full h-full">
            <div
              ref={containerRef}
              className="flex gap-2 transition-transform duration-500 ease-in-out h-full"
            >
              {mediaBlockes?.map(
                (item, index) =>
                  item.blockType === 'mediaBlock' &&
                  typeof item.media === 'object' && (
                    <button
                      type="button"
                      key={index}
                      className={`h-full
                        ${
                          mediaBlockes.length > 5
                            ? 'flex-shrink-0 w-[calc((100%-32px)/5)]'
                            : 'first:pl-0 last:pr-0 p-1 w-full'
                        }`}
                    >
                      <div
                        onClick={() => {
                          setSelectedImage(index)
                        }}
                        className="object-cover h-full "
                      >
                        {item.media.mimeType?.startsWith('image/') ? (
                          <div
                            className={`!h-[64px] sm:!h-[90px] !max-h-[220px] !sm:max-h-[400px] w-full ${
                              index === featuredImageIndex && 'gallerySelectedImage'
                            }`}
                          >
                            <ImageComponent
                              src={`${
                                process.env.NEXT_PUBLIC_PAYLOAD_URL
                                  ? process.env.NEXT_PUBLIC_PAYLOAD_URL
                                  : ''
                              }${item.media.sizes?.thumbnail?.url}`}
                              alt={item.altText ? item.altText : item.media.title}
                            />
                          </div>
                        ) : (
                          <div
                            className={`flex !h-[64px] sm:!h-[90px] !max-h-[220px] !sm:max-h-[400px] w-full items-center justify-center ${
                              index === featuredImageIndex && 'gallerySelectedImage'
                            }`}
                          >
                            <ImageComponent
                              src={`${process.env.NEXT_PUBLIC_PAYLOAD_URL}${
                                typeof item.media.coverImage === 'object' &&
                                item.media.coverImage?.sizes?.thumbnail?.url
                              }`}
                              alt={`${item.media.title}`}
                            />
                            <div className="absolute">
                              <div className="abosolute inset-0 flex items-center justify-center">
                                <div className="absolute flex items-center justify-center">
                                  <div className="rounded-full w-10 h-10 border-[3px] border-[#C13D20] bg-black bg-opacity-70"></div>
                                  <Image
                                    className="absolute left-3 !border-0"
                                    src={'/images/favicons/play icon.svg'}
                                    alt={'Button Image'}
                                    width={20}
                                    height={20}
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </button>
                  ),
              )}
            </div>
          </div>
          {canGoPrev && (
            <button
              className="absolute left-0 top-0 h-full bg-black w-[26px] bg-opacity-45 rounded-tl-lg rounded-bl-lg flex justify-center items-center"
              onClick={handlePrev}
            >
              <FlowiteIconComponent
                iconType={'AngleLeft'}
                className="text-[#E5E7EB]"
                iconClassName="w-[15px] h-[14px]"
              />
            </button>
          )}
          {canGoNext && (
            <button
              className="absolute right-0 top-0 h-full bg-black w-[26px] bg-opacity-45 rounded-tr-lg rounded-br-lg flex justify-center items-center"
              onClick={handleNext}
            >
              <FlowiteIconComponent
                iconType={'AngleRight'}
                className="text-[#E5E7EB]"
                iconClassName="w-[15px] h-[14px]"
              />
            </button>
          )}
        </div>
      )}
    </div>
  )
}

export default EmbeddedGalleryComponent
